if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        // Monfiora — no debounce. Dawn wraps this in debounce(…, 500) because
        // upstream the viewer is a swipe-slider that fires `slideChanged` on
        // every scroll frame, and 500ms of coalescing also happened to hide the
        // unguarded dereference in onSlideChanged below. The cost was a
        // thumbnail highlight that lagged the image by half a second — and,
        // during a momentum flick, indefinitely, because debounce restarts the
        // timer on every event. The viewer is a class-toggle now and never
        // scrolls, so nothing dispatches this; it is kept, guarded and
        // immediate, so the highlight stays correct if a slider ever returns.
        this.elements.viewer.addEventListener('slideChanged', this.onSlideChanged.bind(this));
        // Monfiora — delegate instead of binding per thumbnail.
        // product-info.js replaces the thumbnail <ul>'s innerHTML on every variant
        // change, which destroyed every <li>/<button> that carried a listener. The
        // custom element itself is never replaced on that path, so nothing rebound
        // them and the rail went dead until a full page load. One listener on the
        // host survives any number of DOM swaps.
        this.addEventListener('click', (event) => {
          const item = event.target.closest('[data-target]');
          if (!item || !this.elements.thumbnails || !this.elements.thumbnails.contains(item)) return;
          this.setActiveMedia(item.dataset.target, false);
        });
        // Sync the rail to the slide the server marked active.
        //
        // aria-current is only server-rendered onto a thumbnail whose media id
        // equals the variant's featured_media id. A custom.gallery that lists
        // files which are not product.media renders `-mfgal-N` ids, which never
        // match, so a freshly loaded PDP showed the whole rail with nothing
        // selected while the main image sat on slide 1. No-op when the server
        // already got it right.
        if (!this.elements.thumbnails.querySelector('[aria-current="true"]')) {
          const active =
            this.elements.viewer.querySelector('[data-media-id].is-active') ||
            this.elements.viewer.querySelector('[data-media-id]');
          if (active) {
            this.setActiveThumbnail(
              this.elements.thumbnails.querySelector(`[data-target="${active.dataset.mediaId}"]`)
            );
          }
        }

        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        // currentElement is sliderItemsToShow[currentPage - 1], which is
        // undefined whenever currentPage runs past the list — iOS rubber-band
        // overscroll, or a stale slide cache after a variant swap. Dawn
        // dereferences it directly and throws a TypeError that aborts the
        // handler; the 500ms debounce merely made that rare.
        const currentElement = event.detail?.currentElement;
        if (!currentElement) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            // May be null: custom.gallery decides the rail's contents, and it need
            // not include the variant's featured_media. Dereferencing it threw a
            // TypeError that aborted the rest of updateMedia, leaving the modal on
            // the previous variant.
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail) {
              activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();

        // Monfiora — the class toggle above IS the switch. Dawn additionally
        // defers `parentElement.scrollTo({ left: activeMedia.offsetLeft })` into
        // a bare setTimeout, because upstream the viewer is a horizontal
        // scroller below 750px. That call was the whole failure surface: it
        // depended on an animated, snap-constrained scroll landing on a target
        // measured a macrotask after a class change, it was uncancellable (two
        // quick clicks raced two scrolls on one container), and above 750px it
        // was a silent no-op on a non-scrolling list. The list no longer
        // scrolls on any axis, so the call is gone rather than merely inert.
        //
        // Reading a rect flushes style and layout, so the class change above is
        // already applied here and no deferral is needed to measure correctly.
        const activeMediaRect = activeMedia.getBoundingClientRect();
        // Only pull the page down when the image sits above the viewport.
        if (activeMediaRect.top < -0.5) {
          window.scrollTo({ top: activeMediaRect.top + window.scrollY, behavior: 'smooth' });
        }

        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        // Look the thumbnail up by the media we ACTUALLY activated, not by the id
        // we were asked for.
        //
        // Line 1 of this method already falls back to the first slide when the
        // requested media is absent, and on this store it frequently is: when
        // custom.gallery lists files that were never attached as product.media,
        // the slides are rendered straight from the metafield with ids like
        // `<section>-mfgal-3`, while product-info.js asks for
        // `<section>-<variant.featured_media.id>`. The image therefore fell back
        // to slide 1 while the rail was still queried for the absent id, matched
        // nothing, and returned early — so after a variant change the gallery
        // showed an image with NO thumbnail highlighted, and the live region
        // never announced. Deriving both from `activeMedia` keeps the rail and
        // the viewer on the same slide by construction.
        const activeThumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${activeMedia.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(activeThumbnail);
        if (!activeThumbnail) return;
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        // Dawn assumes every [data-target] wraps a button and throws if one
        // does not, which would abort the caller mid-way through.
        const button = thumbnail.querySelector('button');
        if (button) button.setAttribute('aria-current', true);

        // Dawn keeps the active thumbnail in view with isSlideVisible() +
        // scrollTo({ left }), both horizontal-only. The rail is a vertical
        // column, so every item shares an offsetLeft: isSlideVisible always
        // returned false and the scroll always resolved to scrollTo({left: 0}) —
        // a permanent no-op that left the highlight potentially off-screen.
        // Scroll the rail on its own axis instead, and only when it overflows,
        // so a thumbnail click can never move the page.
        const rail = this.elements.thumbnails.slider;
        if (!rail || rail.scrollHeight <= rail.clientHeight) return;
        const top = thumbnail.offsetTop;
        const bottom = top + thumbnail.offsetHeight;
        if (top < rail.scrollTop) {
          rail.scrollTop = top;
        } else if (bottom > rail.scrollTop + rail.clientHeight) {
          rail.scrollTop = bottom - rail.clientHeight;
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
